import { formatDate } from 'date-fns/format';
import Button from '@/components/Button';
import SpinnerIcon from '@/components/icons/SpinnerIcon';
import Pagination from '@/components/Pagination';
import { InvoiceTransaction } from '@/models/Invoice';
import { formatPrice } from '@/utils/currency';

interface InvoiceTransactionTableProps {
  invoiceTransactions: InvoiceTransaction[];
  page: number;
  itemsPerPage: number;
  totalCount?: number;
  estimatedTotalCount: number;
  isLoading: boolean;
  isFetching: boolean;
  retryingTransactionId?: string;
  onChangePage: (page: number) => void;
  onRetry: (invoiceTransaction: InvoiceTransaction) => void;
}

const InvoiceTransactionTable = ({
  invoiceTransactions,
  page,
  itemsPerPage,
  totalCount,
  estimatedTotalCount,
  isLoading,
  isFetching,
  retryingTransactionId,
  onChangePage,
  onRetry,
}: InvoiceTransactionTableProps) => {
  return (
    <div className="table-container" data-testid="failed-transaction-list">
      <div className="table-wrapper">
        <table className={isLoading ? 'loading' : ''} data-testid="failed-transaction-list-table">
          <thead>
            <tr>
              <th className="min-w-40">Transaction</th>
              <th className="min-w-40">Invoice</th>
              <th className="min-w-32">Amount</th>
              <th className="min-w-40">Failed At</th>
              <th className="min-w-64">Reason</th>
              <th className="min-w-32" />
            </tr>
          </thead>
          <tbody>
            {invoiceTransactions.map((invoiceTransaction) => (
              <tr
                key={invoiceTransaction.id}
                data-testid="failed-transaction-row"
                data-invoice-transaction-id={invoiceTransaction.id}
              >
                <td className="font-mono text-xs text-slate-600">{invoiceTransaction.id}</td>
                <td className="font-mono text-xs text-slate-600">{invoiceTransaction.invoiceId}</td>
                <td>{formatPrice(invoiceTransaction.amount)}</td>
                <td>{formatDate(invoiceTransaction.attemptedAt, 'd MMM yyyy, HH:mm')}</td>
                <td>
                  {invoiceTransaction.failureMessage ||
                    invoiceTransaction.failureCode ||
                    'Unknown error'}
                </td>
                <td className="text-right">
                  <Button
                    variant="secondary"
                    onClick={() => onRetry(invoiceTransaction)}
                    loading={retryingTransactionId === invoiceTransaction.id}
                  >
                    Retry
                  </Button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
        {isLoading ? (
          <div role="status" className="loader">
            <SpinnerIcon />
            <span className="sr-only">Loading...</span>
          </div>
        ) : (
          ''
        )}
      </div>

      {!isLoading && invoiceTransactions.length === 0 && (
        <div className="rounded-xl border border-dashed border-slate-200 bg-slate-50 px-6 py-10 text-center text-sm text-slate-600">
          No failed transactions.
        </div>
      )}

      {isFetching && !isLoading ? (
        <div className="mt-3 flex items-center gap-2 text-sm text-slate-500">
          <SpinnerIcon className="h-4 w-4" />
          Refreshing failed transactions...
        </div>
      ) : null}

      <div className="table-bottom">
        <Pagination
          currentPage={page}
          itemsPerPage={itemsPerPage}
          totalCount={totalCount || 0}
          estimatedTotalCount={estimatedTotalCount}
          onClick={onChangePage}
        />
      </div>
    </div>
  );
};

export default InvoiceTransactionTable;
